/* Osamyynti, varasto ja toimittajat widgets. */
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import InventoryIcon from '@mui/icons-material/Inventory';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import MarkEmailUnreadIcon from '@mui/icons-material/MarkEmailUnread';
import PriceChangeIcon from '@mui/icons-material/PriceChange';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import WarehouseIcon from '@mui/icons-material/Warehouse';

import { dataSources } from '../dataSources';
import BreakdownTile from '../templates/BreakdownTile';
import KpiTile from '../templates/KpiTile';
import ListBody, { SummaryAction, SummaryText } from '../templates/ListBody';
import { ageDays, fmtDay, fmtEur0, fmtEur2, fmtNumber } from '../templates/format';
import { listOf, useHeader, useSource } from '../widgetHooks';

export function LahetteetWidget() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const params = useMemo(() => ({ mode: 'deferred', status: 'open', ordering: 'created_at', page_size: 25 }), []);
  const { data } = useSource(dataSources.partsSales(params, 'open'));
  const items = listOf(data);
  const total = items.reduce((sum, sale) => sum + (Number(sale.total_excl_vat) || 0), 0);
  useHeader({ link: { labelKey: 'dashboard.actions.openLahetteet', to: '/lahetteet' }, empty: Boolean(data) && items.length === 0 }, [data, items.length]);
  const rows = items.map((sale) => {
    const days = ageDays(sale.created_at);
    return {
      key: sale.id,
      icon: ReceiptLongIcon,
      primary: sale.customer_name || '',
      secondary: `#${sale.number}`,
      chip: `${days} ${t('dashboard.chips.daysShort')}`,
      chipTone: days >= 28 ? 'red' : 'neutral',
      right: fmtEur2(sale.total_excl_vat),
      onClick: () => navigate(`/lahetteet/${sale.id}`),
    };
  });
  const summary = data ? <SummaryText parts={[{ strong: true, text: t('dashboard.chips.openCount', { count: data?.count ?? items.length }) }, { text: t('dashboard.chips.total') }, { strong: true, mono: true, text: fmtEur2(total) }]} /> : null;
  return <ListBody rows={rows} totalCount={data?.count} summary={summary} onMore={() => navigate('/lahetteet')} />;
}

export function PickupsWidget() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const params = useMemo(() => ({ mode: 'deferred', special: 'arrived', page_size: 10 }), []);
  const { data } = useSource(dataSources.partsSales(params, 'arrived'));
  const items = listOf(data);
  useHeader({ link: { labelKey: 'dashboard.actions.openLahetteet', to: '/lahetteet' }, empty: Boolean(data) && items.length === 0 }, [data, items.length]);
  const rows = items.map((sale) => ({
    key: sale.id,
    icon: InventoryIcon,
    iconColor: '#2f8c54',
    primary: sale.customer_name || '',
    secondary: `#${sale.number} · ${t('dashboard.chips.arrivedCount', { count: sale.arrived_count ?? 0 })}`,
    action: { label: t('dashboard.actions.open'), onClick: () => navigate(`/lahetteet/${sale.id}`) },
    onClick: () => navigate(`/lahetteet/${sale.id}`),
  }));
  return <ListBody rows={rows} totalCount={data?.count} onMore={() => navigate('/lahetteet')} />;
}

export function UnorderedWidget() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data } = useSource(dataSources.pendingParts());
  const integrations = data?.integrations || [];
  const rows = integrations.map((integration) => {
    const parts = (integration.maintenances || []).flatMap((m) => m.parts || []);
    const total = parts.reduce((sum, part) => sum + (Number(part.purchase_price) || 0) * (Number(part.quantity) || 1), 0);
    const workNumbers = (integration.maintenances || []).map((m) => `#${m.work_number}`).slice(0, 3).join(' ');
    return {
      key: integration.integration_id,
      icon: LocalShippingIcon,
      primary: integration.integration_name || integration.supplier_type,
      secondary: `${t('dashboard.chips.partsCount', { count: parts.length })} · ${workNumbers}`,
      right: total ? fmtEur2(total) : null,
      action: { label: t('dashboard.actions.order'), onClick: () => navigate('/supplier-orders') },
    };
  });
  const partCount = integrations.reduce((sum, integration) => sum + (integration.maintenances || []).reduce((s, m) => s + (m.parts || []).length, 0), 0);
  useHeader({ link: { labelKey: 'dashboard.actions.openOrders', to: '/supplier-orders' }, empty: Boolean(data) && partCount === 0 }, [data, partCount]);
  const summary = data ? (
    <>
      <SummaryText parts={[{ strong: true, text: t('dashboard.chips.partsCount', { count: partCount }) }, { text: t('dashboard.chips.suppliersCount', { count: integrations.length }) }]} />
      <SummaryAction label={t('dashboard.actions.orderAll')} onClick={() => navigate('/supplier-orders')} />
    </>
  ) : null;
  return <ListBody rows={rows} summary={summary} onMore={() => navigate('/supplier-orders')} />;
}

export function LowStockWidget() {
  const navigate = useNavigate();
  const { data } = useSource(dataSources.lowStock());
  const items = listOf(data);
  useHeader({ link: { labelKey: 'dashboard.actions.openInventory', to: '/inventory' }, empty: Boolean(data) && items.length === 0 }, [data, items.length]);
  const rows = items.map((item) => {
    const free = Number(item.quantity_free ?? item.quantity ?? 0);
    return {
      key: item.id,
      icon: Inventory2Icon,
      iconColor: free <= 0 ? '#c53030' : '#94a3b8',
      primary: item.part_name || '',
      secondary: item.part_manufacturer_code || item.shelf_location || '',
      right: `${fmtNumber(free)} / ${fmtNumber(item.reorder_point ?? 0)}`,
      rightTone: free <= 0 ? 'red' : undefined,
      onClick: () => navigate('/inventory'),
    };
  });
  return <ListBody rows={rows} onMore={() => navigate('/inventory')} />;
}

export function InventorySummaryWidget() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data } = useSource(dataSources.inventorySummary());
  return (
    <BreakdownTile
      icon={WarehouseIcon}
      value={fmtNumber(data?.total_items ?? 0)}
      unit={t('dashboard.chips.items')}
      chips={[
        { n: fmtEur0(data?.total_value ?? 0), label: t('dashboard.chips.value') },
        { n: data?.low_stock_count ?? 0, label: t('dashboard.chips.lowStock'), color: '#ed8936', bg: '#fef3e2' },
        { n: data?.negative_count ?? 0, label: t('dashboard.chips.negative'), color: '#c53030', bg: '#fee2e2' },
      ]}
      onClick={() => navigate('/inventory')}
    />
  );
}

export function InboundTile() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data } = useSource(dataSources.inboundCounts());
  const failed = data?.failed ?? 0;
  return (
    <KpiTile
      icon={MarkEmailUnreadIcon}
      value={data?.new ?? 0}
      label={t('dashboard.widgets.inbound.title')}
      sub={failed > 0 ? t('dashboard.chips.failedCount', { count: failed }) : null}
      subTone="red"
      onClick={() => navigate('/supplier-orders')}
    />
  );
}

export function PurchaseInvoicesDueTile() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data } = useSource(dataSources.purchaseLedgerSummary());
  const week = parseFloat(data?.due_this_week?.total || 0);
  const overdue = parseFloat(data?.overdue?.total || 0);
  const toReview = data?.received ?? 0;
  const sub = [
    overdue > 0 ? `${t('purchaseLedger.summary.overdue')} ${fmtEur0(overdue)} (${data?.overdue?.count ?? 0})` : null,
    `${t('purchaseLedger.summary.toReview')} ${toReview}`,
  ]
    .filter(Boolean)
    .join(' · ');
  return (
    <KpiTile
      icon={ReceiptLongIcon}
      value={fmtEur0(week)}
      label={t('purchaseLedger.summary.thisWeek')}
      sub={sub}
      subTone={overdue > 0 ? 'red' : undefined}
      onClick={() => navigate(toReview > 0 && week === 0 ? '/ostolaskut?tab=received' : '/ostolaskut?tab=approved')}
      testId="widget-purchase-invoices-due"
    />
  );
}

export function SuggestionsTile() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data } = useSource(dataSources.partSuggestions());
  return <KpiTile icon={LightbulbIcon} value={data?.pending ?? 0} label={t('dashboard.widgets.suggestions.title')} onClick={() => navigate('/parts')} />;
}

export function SupplierOrdersWidget() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const pending = useSource(dataSources.supplierOrderCount('pending'));
  const confirmed = useSource(dataSources.supplierOrderCount('confirmed'));
  const partial = useSource(dataSources.supplierOrderCount('partially_delivered'));
  const total = (pending.data ?? 0) + (confirmed.data ?? 0) + (partial.data ?? 0);
  return (
    <BreakdownTile
      icon={LocalShippingIcon}
      value={total}
      unit={t('dashboard.chips.openOrders')}
      chips={[
        { n: pending.data ?? 0, label: t('dashboard.chips.pending'), color: '#f6ad55', bg: '#fef3e2' },
        { n: confirmed.data ?? 0, label: t('dashboard.chips.confirmed'), color: '#4299e1', bg: '#ebf4ff' },
        { n: partial.data ?? 0, label: t('dashboard.chips.partiallyDelivered'), color: '#0d9488', bg: '#ccfbf1' },
      ]}
      onClick={() => navigate('/supplier-orders')}
    />
  );
}

export function PriceChangesTile() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data } = useSource(dataSources.priceDrift());
  return <KpiTile icon={PriceChangeIcon} value={data?.count ?? 0} label={t('dashboard.widgets.pricechanges.title')} action={{ label: t('dashboard.actions.update'), onClick: () => navigate('/catalogues/price-drift') }} />;
}

export const _debug = { fmtDay };
